import styled from '@emotion/styled';
import clsx from 'clsx';
import { useState, useEffect, Fragment, ReactNode } from 'react';
import { BsPersonLinesFill } from 'react-icons/bs';
import './sidenav.scss';

import BayLogo from '../BayLogo';
import BayProductSwitcher from '../BayProductSwitcher';
import slugifyText from '../utilities/slugifyText';
import type { VoidCallback } from '../types/callbacks';

import {
	MenuWrapper,
	Menu,
	MenuItem,
	SubMenu,
	SidebarContent,
	SidebarHeader,
	SidebarFooter,
} from './components';
import ConstructedNavItem from './components/ConstructedNavItem';
import MenuToggle from './components/MenuToggle';
import MobileToggler from './components/MobileToggler';
import QuickActions from './components/QuickActions';

// ===== Styled Components =====

interface MobileToggleWrapperProps {
	isActingAsSessionActive?: boolean;
	platformType?: PlatformType;
}

/** @type {import('@emotion/styled').StyledComponent<{}, React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, {}>} */
export const MobileToggleWrapper = styled.div<MobileToggleWrapperProps>`
	display: none;
	padding-left: 16px;
	height: 50px;
	left: 0px;
	top: var(--usertour-banner-height, 0px);
	z-index: 100;
	cursor: pointer;


	@media only screen and (max-width: 575px) {
		top: ${(props) => {
			if (props.isActingAsSessionActive && props.platformType === 'client') {
				return 'calc(var(--actas-banner-height, 85px) + var(--usertour-banner-height, 0px))';
			}

			if (props.isActingAsSessionActive && props.platformType === 'social') {
				return 'calc(var(--actas-banner-height, 77px) + var(--usertour-banner-height, 0px))';
			}

			return 'var(--usertour-banner-height, 0px)';
		}} !important;
	}

	@media only screen and (max-width: 992px) {
		display: flex !important;
	}
`;

// ===== Interfaces =====

type PlatformType = 'financial' | 'social' | 'client' | 'design' | 'sites' | '';

interface NavMenuItem {
	title: string;
	link?: string;
	pageRelatedLinks?: string[];
	icon?: ReactNode;
	sectionLabel?: string;
	subMenuItems?: NavMenuItem[];
	onClick?: VoidCallback;
	isAccessDenied?: boolean;
	id?: string;
	name?: string;
	open?: boolean;
	[key: string]: any;
}

interface SwitcherProps {
	currentlyUsing?: string[];
	isMultiPlatform?: boolean;
	isUpsell?: boolean;
	platform?: string;
	[key: string]: any;
}

interface QuickActionItem {
	icon?: ReactNode;
	toolTip?: string;
	action?: string;
	publicId?: string;
	[key: string]: any;
}

interface SideNavState {
	collapsed: boolean;
	mobileToggled: boolean;
}

interface ProductStages {
	[key: string]: any;
}

interface SideNavProps {
	/** Required array of menuItems */
	menuItems: NavMenuItem[];
	/** Which platform/product are we on */
	platformType: PlatformType;
	/** Props for product switcher */
	switcherProps: SwitcherProps;
	/** Replace the menu header content with your own elements */
	headerContent?: ReactNode;
	/** Replace the menu with your own elements */
	children?: ReactNode;
	/** Add more css classes to the main menu */
	className?: string;
	/** Add your own selector id the main menu */
	id?: string;
	/** Make the menu always open */
	noCollapse?: boolean;
	/** Hide the menu toggle */
	hideToggle?: boolean;
	/** Show the menu header */
	showHeader?: boolean;
	/** Show the menu footer */
	showFooter?: boolean;
	/** contains the object of quick actions */
	quickActionItems?: QuickActionItem[];
	/** Set the show contact modal */
	setShowContactModal?: ((_value: boolean) => void) | null;
	/** Is the contact beta */
	isContactBeta?: boolean;
	/** Update the nav settings */
	updateNavSettings?: ((_value: boolean) => void) | null;
	/** Collapse the body function */
	bodyCollapseFunc?: ((_value: boolean) => void) | null;
	/** True when an acting-as session is currently active */
	isActingAsSessionActive?: boolean;
	/** The side nav state object */
	sideNavState?: SideNavState;
	/** Product stages configuration */
	productStages?: ProductStages;
	/** Set side nav state */
	setSideNavState?: ((_value: boolean | SideNavState) => void);
	/** Listing mode */
	listing?: boolean;
}

// ===== Component =====

const SideNav = (props: SideNavProps) => {
	const {
		children,
		id,
		menuItems,
		platformType,
		className,
		showHeader = false,
		showFooter = true,
		switcherProps,
		quickActionItems = [],
		productStages,
		hideToggle,
		noCollapse = false,
		setShowContactModal = null,
		isContactBeta = false,
		updateNavSettings = null,
		bodyCollapseFunc = null,
		setSideNavState,
		sideNavState = { collapsed: false, mobileToggled: false },
		headerContent,
		isActingAsSessionActive = false,
		listing = false,
	} = props;

	const [windowActiveLink, setWindowActiveLink] = useState<string | null>(null);

	const stylesArray = clsx([
		'bay-sidenav',
		className,
		{
			'actingAsSessionActiveClient': platformType === 'client' && isActingAsSessionActive,
		},
	]);

	let existingArray: string[] | null = JSON.parse(localStorage.getItem('menuIsOpen') ?? 'null');

	const loc = window.location;

	useEffect(() => {
		setActiveLink(loc);
		if (sideNavState?.mobileToggled) {
			toggleSideNav(false);
		}
	}, [loc]);

	const setActiveLink = (loc: Location) => {
		const getUrlParams = (search: string): string => {
			if (!search) return '';
			const params = new URLSearchParams(search).toString();
			return params ? `?${params}` : '';
		};

		if (loc.search) {
			let activeLink = loc.pathname;
			const urlSearchParams = getUrlParams(loc.search);
			if (loc.pathname.split('/').length > 2) {
				if (loc.search.includes('templateCategory=')) {
					const query = loc.search.split('&')[0];
					activeLink += query;
					setWindowActiveLink(activeLink);
				} else if (loc.search.includes('secondaryKeys=')) {
					setWindowActiveLink(activeLink);
				} else {
					setWindowActiveLink(loc.pathname + loc.search);
				}
			} else if (loc.pathname === '/') {
				if (urlSearchParams) {
					setWindowActiveLink(`/${urlSearchParams}`);
				} else {
					setWindowActiveLink(`/${loc.search.split('&')[0]}`);
				}
			} else {
				setWindowActiveLink(loc.pathname);
			}
		} else {
			setWindowActiveLink(loc.pathname);
		}
	};

	const setOpenSubMenu = (isOpen: boolean, menuID: string) => {
		existingArray = JSON.parse(localStorage.getItem('menuIsOpen') ?? 'null');
		const found = existingArray?.find((e) => e === menuID);

		if (found && isOpen) {
			return;
		} else if (found && !isOpen) {
			const newArray = existingArray?.filter((e) => e !== menuID).map((e) => e);
			localStorage.setItem('menuIsOpen', JSON.stringify(newArray));
			return;
		} else if (isOpen) {
			if (existingArray?.length) {
				existingArray.push(menuID);
			} else {
				existingArray = [];
				existingArray.push(menuID);
			}

			localStorage.setItem('menuIsOpen', JSON.stringify(existingArray));
		}
	};

	// Recursive function for checking if any of
	// the sub menu items match the current url
	const recMenuCheck = (item: NavMenuItem): boolean => {
		return checkMenuForActiveLink(item, (menu) => {
			menu.open = true;
		});
	};

	const isSubMenuActive = (menu: NavMenuItem): boolean => checkMenuForActiveLink(menu);

	// Shared utility for recursive submenu checking
	const checkMenuForActiveLink = (
		menu: NavMenuItem,
		callback: ((_menu: NavMenuItem) => void) | null = null,
	): boolean => {
		if (!menu?.subMenuItems?.length) return false;

		const traverse = (items: NavMenuItem[]): boolean => {
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (isMenuItemActive(item?.link, item?.pageRelatedLinks, windowActiveLink)) {
					callback?.(menu); // Optional callback for side effects
					return true;
				}
				if (item?.subMenuItems?.length && traverse(item.subMenuItems)) {
					return true;
				}
			}
			return false;
		};

		return traverse(menu.subMenuItems);
	};

	// Sidenav Collapse
	const collapseSideNav = (bool: boolean) => {
		if (noCollapse && noCollapse === true) {
			return;
		}
		// Set our state
		localStorage.setItem('sidenavCollapse', String(bool));
		updateNavSettings?.(bool);

		// Toggle the body class
		bodyCollapseFunc?.(bool);
	};

	// Toggle the sidenav on mobile widths
	const toggleSideNav = (bool: boolean) => {
		// Set our state
		setSideNavState?.(bool);
		bodyCollapseFunc?.(false);
	};

	// Closes the sidenav on mobile devices when the appRoot is clicked
	useEffect(() => {
		const overlay = document.querySelector('#BaySideNav > div.overlay');

		const handleOverlayClick = () => {
			if (sideNavState.mobileToggled) {
				toggleSideNav(false);
			}
		};

		overlay?.addEventListener('click', handleOverlayClick);

		return () => {
			overlay?.removeEventListener('click', handleOverlayClick);
		};
	}, [sideNavState.mobileToggled]);

	const getStage = (menu: NavMenuItem): any => {
		if (productStages) {
			return (
				productStages[Object.keys(menu).find((key) => productStages[key]) ?? ''] ||
				null
			);
		} else {
			return null;
		}
	};

	/**
	 * Extracts query parameters from a URL, partial URL, or query string.
	 *
	 * Supports:
	 *  - '?page=dashboard'
	 *  - '/?page=email-campaigns&cat=broadcasts'
	 *  - 'https://domain.com/?page=email-campaigns&cat=broadcasts&viewid=all'
	 *
	 * @param {string} searchOrLink - URL or query string
	 * @returns {Object} key-value map of query parameters
	 */

	const getQueryParams = (searchOrLink: string): Record<string, string> => {
		if (!searchOrLink) return {};
		const queryString = searchOrLink.startsWith('?')
			? searchOrLink
			: searchOrLink.split('?')[1] || '';

		return Object.fromEntries(new URLSearchParams(queryString));
	};

	/**
	 * Checks if two ClientBay URLs match based on page and cat parameters
	 * This is used for ClientBay's query-based routing
	 *
	 * @param {string} url1 - First URL
	 * @param {string} url2 - Second URL
	 * @returns {boolean} true if URLs match based on page and cat params
	 */
	const doClientBayUrlsMatch = (url1: string, url2: string): boolean => {
		if (!url1 || !url2) return false;

		// Exact match (important for URLs with additional params like viewid)
		if (url1 === url2) return true;

		const params1 = getQueryParams(url1);
		const params2 = getQueryParams(url2);

		// Check page parameter (must match)
		if (params1.page !== params2.page) return false;

		// If both have cat parameter, they must match
		if (params1.cat && params2.cat && params1.cat !== params2.cat) return false;

		// If only one has cat parameter, they don't match
		if ((params1.cat && !params2.cat) || (!params1.cat && params2.cat)) return false;

		// Page matches and either both have same cat or neither has cat
		return true;
	};

	/**
	 * Checks if two SocialBay URLs match based on path and ALL query parameters
	 * This is used for SocialBay's path-based routing with query params
	 *
	 * @param {string} url1 - First URL
	 * @param {string} url2 - Second URL
	 * @returns {boolean} true if URLs match exactly (path + all query params)
	 */
	const doSocialBayUrlsMatch = (url1: string, url2: string): boolean => {
		if (!url1 || !url2) return false;

		// Exact match (including all query parameters)
		if (url1 === url2) return true;

		// Parse both URLs
		const url1Obj = new URL(url1, window.location.origin);
		const url2Obj = new URL(url2, window.location.origin);

		// Check pathname
		if (url1Obj.pathname !== url2Obj.pathname) return false;

		// Get all query parameters
		const params1 = new URLSearchParams(url1Obj.search);
		const params2 = new URLSearchParams(url2Obj.search);

		// Check if all parameters in url1 exist in url2 with same values
		for (const [key, value] of params1) {
			if (params2.get(key) !== value) {
				return false;
			}
		}

		// Check if all parameters in url2 exist in url1 with same values
		for (const [key, value] of params2) {
			if (params1.get(key) !== value) {
				return false;
			}
		}

		// All query parameters match
		return true;
	};

	/**
	 * Checks if a SocialBay path matches the current path (for nested routes)
	 * This is for menu items without query parameters that should match nested routes
	 *
	 * @param {string} menuPath - Menu item path
	 * @param {string} currentPath - Current browser pathname
	 * @returns {boolean} true if paths match (exact or nested)
	 */
	const isSocialBayPathMatch = (menuPath: string, currentPath: string): boolean => {
		if (!menuPath) return false;

		// Check exact path match
		if (menuPath === currentPath) {
			return true;
		}

		// Check if current path starts with menu path (for nested routes)
		// e.g., menuPath '/design' should match '/design/projects'
		if (menuPath !== '/' && menuPath !== '' && currentPath.startsWith(menuPath)) {
			// Make sure it's a proper segment match (not partial word)
			// e.g., '/des' should not match '/design'
			const nextChar = currentPath.charAt(menuPath.length);
			return nextChar === '' || nextChar === '/' || nextChar === '?';
		}

		return false;
	};

	/**
	 * Determines whether a menu item should be marked as active.
	 * This function uses platformType to determine which matching logic to use.
	 *
	 * Rules for ClientBay (platformType === 'client'):
	 *  - Check page parameter (must match)
	 *  - Check cat parameter (if present in both, must match)
	 *  - Ignore other query parameters
	 *
	 * Rules for SocialBay (platformType !== 'client'):
	 *  - Check exact URL match (path + all query params)
	 *  - Check pageRelatedLinks for exact matches
	 *  - For template library, check specific query params (templateCategory, primaryKeys, secondaryKeys)
	 *  - For other paths, check path matching for nested routes
	 *
	 * @param {string} menuLink - Primary menu link
	 * @param {string[]} pageRelatedLinks - Alternate links associated with the menu
	 * @param {string} currentActiveLink - Current browser URL
	 * @returns {boolean} true if the menu item should be active
	 */
	const isMenuItemActive = (
		menuLink: string | undefined,
		pageRelatedLinks: string[] = [],
		currentActiveLink: string | null,
	): boolean => {
		if (!menuLink) return false;

		const currentPath = window.location.pathname;
		const currentSearch = window.location.search;

		if (platformType === 'client') {
			// ClientBay: Use page and cat parameter matching

			// Check if the menu link matches the current URL
			if (doClientBayUrlsMatch(menuLink, currentActiveLink ?? '')) {
				return true;
			}

			// Check pageRelatedLinks
			if (pageRelatedLinks?.length) {
				for (const relatedLink of pageRelatedLinks) {
					if (doClientBayUrlsMatch(relatedLink, currentActiveLink ?? '')) {
						return true;
					}
				}
			}
		} else {
			// SocialBay: Use exact URL matching or path matching

			// First check exact match for SocialBay URLs (including all query params)
			if (doSocialBayUrlsMatch(menuLink, currentActiveLink ?? '')) {
				return true;
			}

			// Check pageRelatedLinks for exact matches
			if (pageRelatedLinks?.length) {
				for (const relatedLink of pageRelatedLinks) {
					if (doSocialBayUrlsMatch(relatedLink, currentActiveLink ?? '')) {
						return true;
					}
				}
			}

			// Special handling for template library URLs
			if (
				menuLink.startsWith('/project/choose-template') &&
				currentPath === '/project/choose-template'
			) {
				const menuQuery = menuLink.includes('?') ? menuLink.split('?')[1] : null;

				// If menuLink has query parameters, check specific ones
				if (menuQuery) {
					const menuParams = new URLSearchParams(menuQuery);
					const currentParams = new URLSearchParams(currentSearch);

					// Check templateCategory
					if (
						menuParams.has('templateCategory') &&
						currentParams.has('templateCategory')
					) {
						return (
							menuParams.get('templateCategory') ===
							currentParams.get('templateCategory')
						);
					}

					// Check primaryKeys
					if (menuParams.has('primaryKeys') && currentParams.has('primaryKeys')) {
						return menuParams.get('primaryKeys') === currentParams.get('primaryKeys');
					}

					// Check secondaryKeys
					if (
						menuParams.has('secondaryKeys') &&
						currentParams.has('secondaryKeys')
					) {
						return (
							menuParams.get('secondaryKeys') === currentParams.get('secondaryKeys')
						);
					}
				} else {
					// MenuLink has no query params - this is the "All Templates" item
					// It should be active only when there are no query params in current URL
					return !currentSearch;
				}
			}

			// For other SocialBay URLs without query params, check path matching
			if (!menuLink.includes('?')) {
				// Check exact path match
				if (isSocialBayPathMatch(menuLink, currentPath)) {
					return true;
				}

				// Check pageRelatedLinks for path matching
				if (pageRelatedLinks?.length) {
					for (const relatedLink of pageRelatedLinks) {
						if (isSocialBayPathMatch(relatedLink, currentPath)) {
							return true;
						}
					}
				}
			}
		}

		return false;
	};

	// This could be refactored into a more recursive component,
	// but since our max is 3 levels we'll keep it like this for now.
	// NOTE: menuArray / subArray could contain null values.
	return (
		windowActiveLink !== null && (
			<>
				<MobileToggleWrapper
					className="mobile-toggler-wrapper position-fixed w-auto bg-transparent mb-0 flex-wrap align-content-center justify-content-center align-items-center"
					isActingAsSessionActive={isActingAsSessionActive}
					platformType={platformType}
				>
					<MobileToggler
						toggled={sideNavState.mobileToggled}
						toggleFunc={() => toggleSideNav(!sideNavState.mobileToggled)}
					/>
				</MobileToggleWrapper>
				<MenuWrapper
					breakPoint="lg"
					className={stylesArray}
					collapsed={!sideNavState.mobileToggled && sideNavState.collapsed}
					id={id ? id : 'BaySideNav'}
					toggled={sideNavState.mobileToggled}
				>
					{(sideNavState.mobileToggled || !sideNavState.collapsed) && (
						<BayProductSwitcher
							collapsed={!sideNavState.mobileToggled && sideNavState.collapsed}
							toggled={sideNavState.mobileToggled}
							{...switcherProps}
						/>
					)}
					{!hideToggle && (
						<MenuToggle
							collapsed={sideNavState.collapsed}
							platformType={platformType}
							setCollapseState={() => collapseSideNav(!sideNavState.collapsed)}
						/>
					)}
					{showHeader && (
						<SidebarHeader className="border-bottom">
							{quickActionItems.length ? (
								<div className="quick-actions-container">
									{!sideNavState.mobileToggled && sideNavState.collapsed ? (
										<Menu>
											<SubMenu
												className="quick-action-list-icon"
												icon={<BsPersonLinesFill size={14} />}
												showChevron={false}
											>
												{quickActionItems.map((actionItem, index) => {
													return (
														<MenuItem key={index}>
															<QuickActions
																key={index}
																index={index}
																isContactBeta={isContactBeta}
																isMenuLink
																publicId={
																	actionItem.publicId ? actionItem.publicId : ''
																}
																quickActionItem={actionItem as any}
																setShowContactModal={setShowContactModal}
															/>
														</MenuItem>
													);
												})}
											</SubMenu>
										</Menu>
									) : (
										<div className="quick-actions-section d-flex justify-content-evenly align-items-center w-100">
											{quickActionItems.map((actionItem, index) => {
												return (
													<QuickActions
														key={index}
														index={index}
														isContactBeta={isContactBeta}
														isMenuLink={false}
														publicId={
															actionItem.publicId ? actionItem.publicId : ''
														}
														quickActionItem={actionItem as any}
														setShowContactModal={setShowContactModal}
													/>
												);
											})}
										</div>
									)}
								</div>
							) : headerContent ? (
								<div className="sidenav-header-div">
									{headerContent ? headerContent : <></>}
								</div>
							) : null}
						</SidebarHeader>
					)}

					<SidebarContent>
						{children ? (
							<>{children}</>
						) : (
							<Menu>
								{menuItems &&
									menuItems.map((menu, index) => {
										return menu?.subMenuItems?.length <= 0 ? (
											<Fragment key={index}>
												{(menu.title === 'Dashboard' || menu.title === 'Home') && (
													<hr className="mx-n3 d-md-none" />
												)}
												{menu.sectionLabel && (
													<li className="section-label top-level text-400 ps-2 py-3">
														{menu.sectionLabel}
														<br />
													</li>
												)}
												<MenuItem
													accessDenied={menu?.isAccessDenied}
													className={
														isMenuItemActive(
															menu?.link,
															menu?.pageRelatedLinks,
															windowActiveLink,
														)
															? 'active fw-bold'
															: ''
													}
													collapsed={sideNavState.collapsed}
													icon={menu.icon}
													menuItem={menu}
													onClick={menu?.onClick || null}
												>
													<ConstructedNavItem
														collapsed={sideNavState.collapsed}
														listing={listing}
														menuItem={menu as any}
														platformType={platformType as any}
														stage={getStage(menu)}
													/>
												</MenuItem>
											</Fragment>
										) : (
											menu &&
											menu !== null && (
												<SubMenu
													key={index}
													className={
														sideNavState.collapsed && isSubMenuActive(menu)
															? 'active'
															: ''
													}
													defaultOpen={
														(recMenuCheck(menu) &&
															existingArray?.includes(menu.title)) ||
														menu.open
													}
													icon={menu.icon}
													id={`${slugifyText(menu.id ? menu.id : menu.title)}_menu_item`}
													name={`${slugifyText(menu.name ? menu.name : menu.title)}_menu_item`}
													stage={getStage(menu)}
													title={menu.title}
													onOpenChange={(open) =>
														setOpenSubMenu(open, menu.id ? menu.id : menu.title)
													}
												>
													{menu?.subMenuItems?.map((menu, index) => {
														return menu?.subMenuItems?.length <= 0 ? (
															<Fragment key={index}>
																{menu.sectionLabel && (
																	<li className="section-label text-400 ps-2 py-3">
																		{menu.sectionLabel}
																		<br />
																	</li>
																)}
																<MenuItem
																	className={
																		isMenuItemActive(
																			menu?.link,
																			menu?.pageRelatedLinks,
																			windowActiveLink,
																		)
																			? 'active fw-bold'
																			: ''
																	}
																	icon={menu.icon}
																>
																	<ConstructedNavItem
																		subItem
																		collapsed={sideNavState.collapsed}
																		menuItem={menu as any}
																		platformType={platformType as any}
																		stage={getStage(menu)}
																	/>
																	<span className="bay-sidenav-arrow-wrapper">
																		<span className="bay-sidenav-arrow" />
																	</span>
																</MenuItem>
															</Fragment>
														) : (
															menu &&
															menu !== null && (
																<SubMenu
																	key={index}
																	className={
																		menu.subMenuItems?.length ? 'second-sub' : ''
																	}
																	defaultOpen={
																		(recMenuCheck(menu) &&
																			existingArray?.includes(menu.title)) ||
																		menu.open
																	}
																	icon={menu.icon}
																	id={`${slugifyText(
																		menu.id ? menu.id : menu.title,
																	)}_menu_item`}
																	name={`${slugifyText(
																		menu.name ? menu.name : menu.title,
																	)}_menu_item`}
																	title={menu.title}
																	onOpenChange={(open) =>
																		setOpenSubMenu(open, menu.title)
																	}
																>
																	{menu?.subMenuItems?.map((menu, index) => {
																		return menu?.subMenuItems?.length <= 0 ? (
																			<Fragment key={index}>
																				{menu.sectionLabel && (
																					<li className="section-label text-400 ps-2 py-3">
																						{menu.sectionLabel}
																						<br />
																					</li>
																				)}
																				<MenuItem
																					className={
																						isMenuItemActive(
																							menu?.link,
																							menu?.pageRelatedLinks,
																							windowActiveLink,
																						)
																							? 'active'
																							: ''
																					}
																				>
																					<ConstructedNavItem
																						subItem
																						collapsed={sideNavState.collapsed}
																						menuItem={menu as any}
																						platformType={platformType as any}
																						stage={getStage(menu)}
																					/>
																					<span className="bay-sidenav-arrow-wrapper">
																						<span className="bay-sidenav-arrow" />
																					</span>
																				</MenuItem>
																			</Fragment>
																		) : (
																			menu &&
																			menu !== null && (
																				<SubMenu
																					key={index}
																					className={
																						menu.subMenuItems?.length
																							? 'third-sub'
																							: ''
																					}
																					defaultOpen={
																						(recMenuCheck(menu) &&
																							existingArray?.includes(
																								menu.title,
																							)) ||
																						menu.open
																					}
																					icon={menu.icon}
																					id={`${slugifyText(
																						menu.id ? menu.id : menu.title,
																					)}_menu_item`}
																					name={`${slugifyText(
																						menu.name ? menu.name : menu.title,
																					)}_menu_item`}
																					title={menu.title}
																					onOpenChange={(open) =>
																						setOpenSubMenu(open, menu.title)
																					}
																				>
																					{menu?.subMenuItems?.map(
																						(menu, index) => {
																							return (
																								<Fragment key={index}>
																									{menu.sectionLabel && (
																										<li className="section-label text-400 ps-3 py-3">
																											{menu.sectionLabel}
																											<br />
																										</li>
																									)}
																									<MenuItem
																										className={
																											isMenuItemActive(
																												menu?.link,
																												menu?.pageRelatedLinks,
																												windowActiveLink,
																											)
																												? 'active'
																												: ''
																										}
																										icon={menu.icon}
																									>
																										<ConstructedNavItem
																											subItem
																											collapsed={
																												sideNavState.collapsed
																											}
																											menuItem={menu as any}
																											platformType={platformType as any}
																											stage={getStage(menu)}
																										/>
																									</MenuItem>
																								</Fragment>
																							);
																						},
																					)}
																				</SubMenu>
																			)
																		);
																	})}
																</SubMenu>
															)
														);
													})}
												</SubMenu>
											)
										);
									})}
							</Menu>
						)}
					</SidebarContent>
					{showFooter && (
						<SidebarFooter>
							{!sideNavState.collapsed ? (
								<BayLogo logoStyle="powered" platform={'' as any} size="28px" />
							) : (
								<BayLogo logoStyle="icon" platform={'' as any} size="32px" />
							)}
						</SidebarFooter>
					)}
				</MenuWrapper>
			</>
		)
	);
};

export default SideNav;
